import React, { FC, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, Flex, Text } from "@radix-ui/themes";
import { DateTime } from "luxon";

import { api } from "@/modules/api";
import { atlasRoutes } from "@/app/router/routes";
import { UuidFieldRow } from "@/components/table/uuid-field-row";

type AppEventSummary = {
  id: string;
  associated_space_account_id?: string | null;
  kind: string;
  occurred_at: string;
};

export const AppEventsRootPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const pageSize = 50;
  const initialQuery = searchParams.get("query") ?? "";
  const initialSpaceId = searchParams.get("space") ?? "";
  const initialKind = searchParams.get("kind") ?? "";

  const [searchTerm, setSearchTerm] = useState<string>(initialQuery);
  const [inputValue, setInputValue] = useState<string>(initialQuery);
  const [spaceAccountId, setSpaceAccountId] = useState<string>(initialSpaceId);
  const [kind, setKind] = useState<string>(initialKind);

  useEffect(() => {
    setInputValue(searchParams.get("query") ?? "");
    setSpaceAccountId(searchParams.get("space") ?? "");
    setKind(searchParams.get("kind") ?? "");
    setSearchTerm(searchParams.get("query") ?? "");
  }, [searchParams]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["app-events-search", searchTerm, spaceAccountId, kind],
    queryFn: async () => {
      const body: Record<string, unknown> = { limit: pageSize };
      if (searchTerm) body.query = searchTerm;
      if (spaceAccountId) body.filter_by_space_account_id = spaceAccountId;
      if (kind) body.filter_by_kind = kind;
      const resp = await api.post("/internal/v1/app-events/search", { body });
      return resp;
    },
    enabled: Boolean(searchTerm || spaceAccountId || kind),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const handleSearch = () => {
    if (inputValue) {
      searchParams.set("query", inputValue);
    } else {
      searchParams.delete("query");
    }
    if (spaceAccountId) {
      searchParams.set("space", spaceAccountId);
    } else {
      searchParams.delete("space");
    }
    if (kind) {
      searchParams.set("kind", kind);
    } else {
      searchParams.delete("kind");
    }
    setSearchParams(searchParams);
  };

  const handleView = (id: string) => {
    navigate(atlasRoutes.appEventsView({ params: { appEventId: id } }).path);
  };

  const tableData = useMemo<AppEventSummary[]>(() => data?.data?.results ?? [], [data]);

  return (
    <Container>
      <Flex justify="between" align="center" mb="4">
        <div>
          <h1>App Events</h1>
          <DescriptionText>
            Search by query, with optional Space Account and Kind filters.
          </DescriptionText>
        </div>
      </Flex>

      <SearchRow>
        <input
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") handleSearch();
          }}
          placeholder="Search by event id, space account id, or kind"
        />
        <input
          value={spaceAccountId}
          onChange={e => setSpaceAccountId(e.target.value)}
          placeholder="Filter by Space Account ID (optional)"
        />
        <input
          value={kind}
          onChange={e => setKind(e.target.value)}
          placeholder="Filter by Kind (optional)"
        />
        <Button onClick={handleSearch}>Search</Button>
      </SearchRow>

      {isLoading && <div>Loading...</div>}
      {isError && <div>Error fetching: {error.message}</div>}

      {tableData.length > 0 ? (
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Occurred At</th>
                <th>ID</th>
                <th>Space Account</th>
                <th>Kind</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map(row => (
                <tr key={row.id}>
                  <td>
                    {row.occurred_at
                      ? DateTime.fromISO(row.occurred_at).toFormat("MMM d, yyyy h:mm a")
                      : "-"}
                  </td>
                  <td>
                    <UuidFieldRow id={row.id as unknown as string} />
                  </td>
                  <td>
                    <UuidFieldRow
                      id={(row.associated_space_account_id as unknown as string) ?? null}
                    />
                  </td>
                  <td>{row.kind}</td>
                  <td>
                    <Button variant="soft" onClick={() => handleView(row.id)}>
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      ) : (
        !isLoading && (searchTerm || spaceAccountId || kind) && <div>No events found.</div>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const DescriptionText = styled(Text)`
  display: block;
  color: var(--gray-12);
`;

const SearchRow = styled(Flex)`
  gap: 8px;
  margin: 12px 0 16px 0;
  flex-wrap: wrap;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;

  th,
  td {
    text-align: left;
    padding: 8px 6px;
    border-bottom: 1px solid var(--gray-5);
    white-space: nowrap;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
`;
